
import { defineComponent, ref, computed, PropType, Ref, watch } from "vue";
import {
  useGetProfilesByLevelOfUseQuery,
  ProfileType,
  LevelOfUse,
  GetProfilesByLevelOfUseQuery,
  useGetOrganizationQuery,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import VDPopupListbox from "@/components/pickers/VDPopupListbox.vue";
import { VDPopupListboxClass } from "@/components/pickers/VDPopupListbox";
import GroupedItems from "@/models/GroupedItems";

export default defineComponent({
  name: "UserRoleProfilePicker",
  components: {
    VDPopupListbox,
  },
  emits: ["profilesSelected"],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    addedProfiles: {
      type: Array as PropType<ProfileType[]>,
      default: () => [],
    },
    organizationId: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    // VDOM
    const listbox = ref<VDPopupListboxClass>();

    // Refs
    const isEnabled = ref(true);

    // Data - Query for user-level profiles
    const getProfilesResponse = useGetProfilesByLevelOfUseQuery(
      () => ({ levelOfUse: LevelOfUse.User }),
      () => ({
        enabled: isEnabled.value,
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      })
    );

    // Get organization roles to filter against
    const getOrganizationResponse = useGetOrganizationQuery(
      () => ({
        organizationId: props.organizationId,
        pageParams: { pageSize: 1, currentPage: 1 }, // Minimal page params, we only need assigneeRoles
      }),
      () => ({
        enabled: isEnabled.value && !!props.organizationId,
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      })
    );

    // Computed
    const loading = computed(() => {
      return (
        getProfilesResponse.loading.value ||
        getOrganizationResponse.loading.value
      );
    });

    const error = computed(() => {
      return (
        getProfilesResponse.error.value || getOrganizationResponse.error.value
      );
    });

    // Get organization's assigned roles for filtering
    const organizationRoles = computed(() => {
      const org = useResult(getOrganizationResponse.result).value;
      return org?.assigneeRoles || [];
    });

    function onProfileSelected(profile: any) {
      // Check if we have organization data
      if (!props.organizationId) {
        console.error("No organization ID provided to UserRoleProfilePicker");
        return;
      }

      // Filter roles to only include those assigned to the organization
      const availableRoles: any[] = [];
      const unavailableRoles: any[] = [];

      if (profile.roles && profile.roles.length > 0) {
        profile.roles.forEach((role: any) => {
          const isAssignedToOrg = organizationRoles.value.some(
            (orgRole: any) => orgRole?.roleId === role.roleId
          );

          if (isAssignedToOrg) {
            availableRoles.push(role);
          } else {
            unavailableRoles.push(role);
          }
        });
      }

      // Emit event with filtered roles and information about unavailable roles
      emit("profilesSelected", {
        profile,
        availableRoles,
        unavailableRoles,
      });
    }

    function getAssignableRolesCount(profile: any): number {
      if (!profile.roles || profile.roles.length === 0) {
        return 0;
      }

      return profile.roles.filter((role: any) => {
        return organizationRoles.value.some(
          (orgRole: any) => orgRole?.roleId === role.roleId
        );
      }).length;
    }

    return {
      groupedProfiles: useGroupedProfilesByApplication(
        getProfilesResponse.result
      ),
      listbox,
      loading,
      error,
      onProfileSelected,
      getAssignableRolesCount,
      toggle: (event: Event) => {
        if (!props.organizationId) {
          console.error(
            "Cannot open UserRoleProfilePicker: No organization ID provided"
          );
          return;
        }
        isEnabled.value = true;
        listbox.value?.toggle(event);
      },
    };
  },
});

function useGroupedProfilesByApplication(
  profilesQuery: Ref<GetProfilesByLevelOfUseQuery | undefined>
) {
  const groupedProfiles = ref<GroupedItems<ProfileType>[]>([]);

  watch(
    () => profilesQuery.value,
    (newValue) => {
      const map: { [name: string]: ProfileType[] } = {};

      newValue?.profilesByLevelOfUse?.forEach((element: any) => {
        if (element?.application?.name && map[element.application.name]) {
          map[element.application.name].push(element);
        } else if (element?.application?.name) {
          map[element.application.name] = [element];
        }
      });

      groupedProfiles.value = Object.keys(map)
        .sort()
        .map((element) => {
          return {
            name: element,
            items: map[element].sort(
              (a, b) => a.name?.localeCompare(b.name ?? "") ?? -1
            ),
          };
        });
    },
    { deep: true, immediate: true }
  );

  return groupedProfiles;
}

// Export type for parent components
export declare class UserRoleProfilePickerClass {
  toggle(event: Event): void;
  hide(): void;
}
